import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtTotals, STATUS_META, formatAxiosError } from "../lib/api";
import { GROUP_STATUS_META, ungroupableReason, faultsOf } from "../lib/partnerGroups";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import GroupFaults from "../components/GroupFaults";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { ArrowLeft, Pencil, Plus, Trash2, ChevronRight } from "lucide-react";

const plural = (n, one, many) => `${n} ${n > 1 ? many : one}`;

const CANCEL_CONFIRM = {
  brouillon: "Annuler ce dossier groupé ? Les propositions redeviennent des propositions seules, rien n'est supprimé.",
  envoyé: "Annuler ce dossier groupé ? Le lien envoyé au gestionnaire ne fonctionnera plus. Les propositions redeviennent des propositions seules, rien n'est supprimé.",
};

/**
 * Un dossier groupé du partenaire : les villas, leur état, leurs montants. Avant l'envoi (état « brouillon »), le partenaire renomme,
 * ajoute et retire des villas, ou annule. Après l'envoi les villas sont figées ; signé ou annulé : lecture seule.
 * L'état et les montants viennent du serveur (calculés depuis les villas) : rien n'est recalculé ici.
 */
export default function PartnerGroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [busy, setBusy] = useState(null); // « rename » | « cancel » | « add » | « remove:<id> » : une seule action à la fois
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [adding, setAdding] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [picked, setPicked] = useState([]);
  const [faults, setFaults] = useState([]);
  useDocumentTitle(g?.title ? `${g.title} · Dossier` : "Dossier groupé");
  // Focus clavier : un élément démonté ne laisse pas le focus tomber sur <body> (renommage, panneau d'ajout).
  const pencilRef = useRef(null);
  const addToggleRef = useRef(null);
  const addPanelRef = useRef(null);
  const wasRenaming = useRef(false);
  const wasAdding = useRef(false);
  useEffect(() => {
    if (wasRenaming.current && !renaming) pencilRef.current?.focus();
    wasRenaming.current = renaming;
  }, [renaming]);
  useEffect(() => {
    if (adding) addPanelRef.current?.focus();
    if (wasAdding.current && !adding) addToggleRef.current?.focus();
    wasAdding.current = adding;
  }, [adding]);

  const load = () => api.get(`/partner/proposal-groups/${id}`).then((r) => setG(r.data));

  useEffect(() => {
    // Redirections en « replace » : le bouton Retour ne ramène jamais sur un dossier supprimé ou introuvable.
    load().catch((e) => { toast.error(formatAxiosError(e, "Dossier introuvable")); navigate("/partner/proposals", { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!g) return <div className="p-12 text-muted-foreground">Chargement…</div>;

  const meta = GROUP_STATUS_META[g.status] || GROUP_STATUS_META.brouillon;
  const editable = g.status === "brouillon"; // villas modifiables avant l'envoi seulement
  const cancellable = g.status === "brouillon" || g.status === "envoyé";
  const currency = g.currency || "EUR";
  const memberIds = new Set((g.members || []).map((m) => m.id));
  const eligible = (candidates || []).filter((p) => !memberIds.has(p.id) && !ungroupableReason(p, g.client_id));

  const startRename = () => { setNewTitle(g.title); setRenaming(true); };
  const saveTitle = async () => {
    setBusy("rename");
    try {
      const { data } = await api.put(`/partner/proposal-groups/${id}`, { title: newTitle.trim() });
      setG(data);
      setRenaming(false);
      toast.success("Dossier renommé");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(null); }
  };

  const removeMember = async (m) => {
    const last = (g.members || []).length === 1;
    const ask = last
      ? `Retirer « ${m.title} » du dossier ? C'est la dernière villa : le dossier sera supprimé avec elle. La proposition redevient seule, rien d'autre n'est supprimé.`
      : `Retirer « ${m.title} » du dossier ? Elle redevient une proposition seule, rien n'est supprimé.`;
    if (!window.confirm(ask)) return;
    setBusy(`remove:${m.id}`);
    try {
      const { data } = await api.delete(`/partner/proposal-groups/${id}/members/${m.id}`);
      if (data?.deleted) {
        toast.success("Dossier supprimé avec sa dernière villa");
        navigate("/partner/proposals", { replace: true });
        return;
      }
      toast.success("Villa retirée du dossier");
      await load();
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setBusy(null); }
  };

  const openAdd = () => {
    setAdding(true); setPicked([]); setFaults([]); setCandidates(null);
    api.get("/partner/proposals").then((r) => setCandidates(Array.isArray(r.data) ? r.data : [])).catch((e) => {
      // Un échec n'est pas une liste vide : on le dit et on referme (le même bouton sert de nouvel essai).
      toast.error(formatAxiosError(e, "Impossible de charger vos propositions"));
      setAdding(false);
    });
  };
  const closeAdd = () => { setAdding(false); setPicked([]); setFaults([]); };
  const togglePick = (pid) => setPicked((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]));
  const addPicked = async () => {
    setBusy("add"); setFaults([]);
    try {
      const { data } = await api.post(`/partner/proposal-groups/${id}/members`, { proposal_ids: eligible.filter((p) => picked.includes(p.id)).map((p) => p.id) });
      setG(data);
      closeAdd();
      toast.success("Villas ajoutées au dossier");
    } catch (e) {
      const listed = faultsOf(e);
      if (listed.length) setFaults(listed); else toast.error(formatAxiosError(e));
    } finally { setBusy(null); }
  };

  const cancelGroup = async () => {
    if (!window.confirm(CANCEL_CONFIRM[g.status])) return;
    setBusy("cancel");
    try {
      await api.delete(`/partner/proposal-groups/${id}`);
      toast.success("Dossier annulé");
      navigate("/partner/proposals", { replace: true });
    } catch (e) { toast.error(formatAxiosError(e)); setBusy(null); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-3xl" data-testid="partner-group-detail">
      <button onClick={() => navigate("/partner/proposals")} data-testid="partner-group-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 min-h-[44px]">
        <ArrowLeft className="w-4 h-4" /> Retour aux propositions
      </button>

      <div className="mb-6">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Dossier groupé</p>
        {renaming ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input autoFocus value={newTitle} maxLength={120} onChange={(e) => setNewTitle(e.target.value)} aria-label="Nom du dossier" data-testid="partner-group-rename-input" className="rounded-sm min-h-[44px] flex-1 min-w-[12rem]" />
            <Button onClick={saveTitle} disabled={busy === "rename" || !newTitle.trim() || newTitle.trim() === g.title} data-testid="partner-group-rename-save" className="rounded-sm min-h-[44px]">
              {busy === "rename" ? "Enregistrement…" : "Enregistrer"}
            </Button>
            <Button variant="outline" onClick={() => setRenaming(false)} data-testid="partner-group-rename-cancel" className="rounded-sm min-h-[44px]">Annuler</Button>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight break-words min-w-0 flex-1">{g.title}</h1>
            {g.status !== "annulé" && (
              <button ref={pencilRef} onClick={startRename} data-testid="partner-group-rename" aria-label="Renommer le dossier"
                className="shrink-0 -mt-1 text-muted-foreground hover:text-primary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
                <Pencil className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        <span data-testid="partner-group-status" className={`inline-block mt-2 text-[11px] px-2 py-0.5 rounded-sm border font-medium ${meta.color}`}>{meta.label}</span>
      </div>

      {g.status === "annulé" && (
        <div data-testid="partner-group-cancelled-note" className="bg-rose-50 border border-rose-200 text-rose-800 rounded-sm px-4 py-3 mb-6 text-sm">
          Ce dossier est annulé : ses villas sont redevenues des propositions seules.
        </div>
      )}

      <div className="bg-card border border-border rounded-sm p-5 sm:p-6 mb-6 space-y-2" data-testid="partner-group-summary">
        <p className="text-sm">
          Client : <span className="font-medium">{g.client_name || "—"}</span>
          <span className="text-muted-foreground"> · {plural(g.member_count, "villa", "villas")}</span>
          {g.status !== "brouillon" && g.status !== "annulé" && <span className="text-muted-foreground"> · {g.signed_count} signée{g.signed_count > 1 ? "s" : ""}</span>}
        </p>
        <p className="text-sm font-medium font-mono">{fmtTotals(g.total_one_shot, g.total_recurring, currency)}</p>
      </div>

      <div className="space-y-3 mb-6">
        {(g.members || []).map((m) => {
          const st = STATUS_META[m.status] || STATUS_META.brouillon;
          return (
            <div key={m.id} className="bg-card border border-border rounded-sm p-4 sm:p-5" data-testid={`partner-group-member-${m.id}`}>
              <div className="flex items-start justify-between gap-3">
                <button onClick={() => navigate(`/partner/proposals/${m.id}`)} data-testid={`partner-group-member-open-${m.id}`} className="min-w-0 text-left flex-1">
                  <p className="font-medium flex items-center gap-2 flex-wrap">
                    <span className="break-words min-w-0">{m.title}</span>
                    {m.signed
                      ? <span className="text-[11px] px-2 py-0.5 rounded-sm border font-medium bg-emerald-50 text-emerald-700 border-emerald-200">Signée</span>
                      : <span className={`text-[11px] px-2 py-0.5 rounded-sm border font-medium ${st.color}`}>{st.label}</span>}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">{m.client_name || "Sans client"}</p>
                </button>
                <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0 mt-1" aria-hidden="true" />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                <span className="font-mono text-sm font-medium">{fmtTotals(m.total_one_shot, m.total_recurring, m.currency || currency)}</span>
                {editable && (
                  <button onClick={() => removeMember(m)} disabled={busy !== null} data-testid={`partner-group-member-remove-${m.id}`}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 min-h-[44px] px-1">
                    <Trash2 className="w-3.5 h-3.5" /> Retirer du dossier
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editable && (
        <div className="mb-6">
          {!adding ? (
            <Button ref={addToggleRef} variant="outline" onClick={openAdd} data-testid="partner-group-add-toggle" className="rounded-sm min-h-[44px]">
              <Plus className="w-4 h-4 mr-1.5" /> Ajouter des villas
            </Button>
          ) : (
            <div ref={addPanelRef} tabIndex={-1} className="bg-card border border-border rounded-sm p-4 sm:p-5 space-y-3 outline-none" data-testid="partner-group-add-panel">
              <p className="font-medium">Ajouter des villas à ce dossier</p>
              {candidates === null ? (
                <p className="text-sm text-muted-foreground">Chargement…</p>
              ) : eligible.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="partner-group-add-empty">Aucune autre proposition en brouillon pour ce client.</p>
              ) : (
                <div className="space-y-2">
                  {eligible.map((p) => (
                    <label key={p.id} data-testid={`partner-group-candidate-${p.id}`} className="flex items-start gap-3 border border-border rounded-sm px-3 py-2.5 min-h-[44px] cursor-pointer">
                      <input type="checkbox" checked={picked.includes(p.id)} onChange={() => togglePick(p.id)} aria-label={`Ajouter ${p.title}`} className="w-5 h-5 mt-0.5 accent-[hsl(var(--primary))] shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block break-words text-sm font-medium">{p.title}</span>
                        <span className="block font-mono text-xs text-muted-foreground">{fmtTotals(p.total_one_shot, p.total_recurring, p.currency)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <GroupFaults faults={faults} />
              <div className="flex flex-wrap gap-2">
                <Button onClick={addPicked} disabled={busy === "add" || picked.length === 0} data-testid="partner-group-add-submit" className="rounded-sm min-h-[44px]">
                  {busy === "add" ? "Ajout…" : picked.length ? `Ajouter ${plural(picked.length, "villa", "villas")}` : "Ajouter"}
                </Button>
                <Button variant="outline" onClick={closeAdd} className="rounded-sm min-h-[44px]">Fermer</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {cancellable && (
        <Button variant="outline" onClick={cancelGroup} disabled={busy !== null} data-testid="partner-group-cancel" className="rounded-sm min-h-[44px] text-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4 mr-1.5" /> {busy === "cancel" ? "Annulation…" : "Annuler le dossier"}
        </Button>
      )}
    </div>
  );
}
